import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Params } from '@angular/router';

import {
  ISnListResponse,
  ISnListQueryParameters,
  ISnFeedQueryParameters,
  extractCursor,
} from '@lib/api';

import { SnListDataSource } from './list.data-source';
import { ISnListItem } from './list.interfaces';
import { SnListOperation } from './list.utils';

export class SnListReference<M extends ISnListItem> {
  private static readonly DEFAULT_PAGE_SIZE = 20;

  public operation: SnListOperation = SnListOperation.Init;

  public fetchFn: (params: ISnListQueryParameters) => Observable<ISnListResponse<M>>;
  public feedFn: (params: ISnFeedQueryParameters) => Observable<M[]>;

  public reloadByQueryParams: boolean;

  public enableCursorPagination = false;

  private readonly _load$ = new Subject<void>();
  private readonly _feed$ = new Subject<number>();
  private readonly _reload$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  private readonly _sort$ = new BehaviorSubject<Sort>({
    active: null,
    direction: null,
  });

  private _inited = false;
  private _complete: boolean;

  // Page-based pagination state
  private _page = 1;
  private _totalPages: number;

  // Cursor-based pagination state
  private _cursor: string | null = null;
  private _hasNext = false;

  private _queryParams: Params;
  private readonly _dataSource: SnListDataSource<M>;
  private readonly _loaded$ = new Subject<boolean>();

  constructor(private readonly _activatedRoute: ActivatedRoute) {
    this._dataSource = new SnListDataSource();
    this._initSubscriptions();
  }

  get inited(): boolean {
    return this._inited;
  }
  get dataSource(): SnListDataSource<M> {
    return this._dataSource;
  }

  get load$(): Observable<void> {
    return this._load$.asObservable();
  }
  get feed$(): Observable<number> {
    return this._feed$.asObservable();
  }
  get reload$(): Observable<void> {
    return this._reload$.asObservable();
  }
  get loaded$(): Observable<boolean> {
    return this._loaded$.asObservable();
  }

  get isComplete(): boolean {
    if (this.isUsingCursorPagination) {
      return !this._hasNext;
    }

    return this._complete || (this._totalPages && this._page >= this._totalPages);
  }

  private get isUsingCursorPagination(): boolean {
    return this.enableCursorPagination || this._cursor !== null || this._hasNext;
  }

  public load(): void {
    this._load$.next();
  }

  public feed(count: number): void {
    this._feed$.next(count);
  }

  public reload(): void {
    this.operation = SnListOperation.Reload;

    this._inited = false;

    // Reset page-based pagination state
    this._page = 1;
    this._totalPages = null;

    // Reset cursor-based pagination state
    this._cursor = null;
    this._hasNext = false;

    this._complete = false;

    this._dataSource.clear();
    this._reload$.next();
  }

  public nextPage(): void {
    if (this.isComplete) {
      return;
    }

    if (!this.isUsingCursorPagination) {
      this._page++;
    }

    this.load();
  }

  public prevPage(): void {
    if (this._page === 1) {
      return;
    }

    this._page--;
    this.load();
  }

  public destroy(): void {
    this._load$.complete();
    this._reload$.complete();
    this._sort$.complete();

    this._dataSource.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  private get queryParams() {
    const baseParams = this.reloadByQueryParams ? this._queryParams : {};

    // Cursor-based pagination
    if (this._cursor || this.enableCursorPagination) {
      return {
        ...baseParams,
        limit: baseParams.limit ?? SnListReference.DEFAULT_PAGE_SIZE,
        cursor: this._cursor ?? undefined, // undefined on first request, then set from response for subsequent pages
        // Backend requires: page/page_size absent
        page: undefined,
        pageSize: undefined,
        page_size: undefined,
      };
    }

    // Page-based pagination by default
    return {
      pageSize: SnListReference.DEFAULT_PAGE_SIZE,
      page: this._page,
      ...baseParams,
    };
  }

  private _initSubscriptions(): void {
    this._activatedRoute.queryParams
      .pipe(
        tap((params) => (this._queryParams = params)),
        filter(() => this.reloadByQueryParams && this.inited),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this.reload());

    merge(this._load$, this._reload$)
      .pipe(
        debounceTime(300),
        filter(() => !!this.fetchFn),
        map(() => this.queryParams),
        switchMap((query) =>
          this.fetchFn(query).pipe(
            catchError(() =>
              of({
                results: [],
                ...(this.isUsingCursorPagination
                  ? { next: null, previous: null }
                  : { totalPages: this._totalPages || 1 }),
              }),
            ),
          ),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe((response: ISnListResponse<any>) => {
        this._inited = true;
        this._handlePaginationResponse(response);
        this._updateDataSource(response.results);
        this._loaded$.next(true);
      });

    this._feed$
      .pipe(
        debounceTime(50),
        filter(() => !!this.feedFn),
        map((count) => {
          const data = this._dataSource.data.slice();
          const lastItem = data.pop();

          return {
            startFrom: lastItem.id,
            count,
            ...this.queryParams,
          };
        }),
        switchMap((params) => this.feedFn(params)),
        catchError(() => of([])),
        takeUntil(this._destroy$),
      )
      .subscribe((items) => {
        this._updateDataSource(items);
      });

    this._sort$
      .pipe(
        debounceTime(50),
        filter(() => this.operation !== SnListOperation.Init),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.reload();
      });

    this._dataSource.itemsDeleted$.pipe(takeUntil(this._destroy$)).subscribe((items) => {
      this.feed(items.length);
    });
  }

  private _handlePaginationResponse(response: ISnListResponse<any>): void {
    const hasCursorFields = response.next !== undefined || response.previous !== undefined;

    if (hasCursorFields) {
      this._hasNext = !!response.next;
      this._cursor = this._hasNext ? extractCursor(response.next) : null;
      this._complete = !this._hasNext;
    } else {
      this._totalPages = response.totalPages;
      if (response.results.length === 0 && !response.totalPages && !response.skipError) {
        this._complete = true;
      }
    }
  }

  private _updateDataSource(items: any[]): void {
    if (this.operation === SnListOperation.Init || this.operation === SnListOperation.Reload) {
      this._dataSource.clear();
    }

    this._dataSource.insertItem(...items);
    this.operation = SnListOperation.Idle;
  }
}
